import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import AppContext from './AppContext.jsx';
import dbClient from './db/client.js';
import auth from './db/auth.js';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import formatTime from './utils/formatTime'
import syncDb from './serviceWorker/syncDb'
import Modal from 'react-bootstrap/Modal';
import apiQueue from './db/apiQueue.js';
import { customerName, parseApiUrl } from './helpers.js';
import OfflineOrdersTable from './components/OfflineOrdersTable.jsx';
import Toast from './Toast.jsx';

function OfflineQueue() {
  const { app } = useContext(AppContext);
  const navigate = useNavigate();
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [orderNames, setOrderNames] = useState({});
  const [toastTrigger, setToastTrigger] = useState(0);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadQueueItems();

    // Check for toast message from localStorage (for page reloads)
    const storedToast = localStorage.getItem('offlineQueueToast');
    if (storedToast) {
      localStorage.removeItem('offlineQueueToast'); // Clear it after reading
      showToastMessage(storedToast);
    }
  }, []);

  async function loadQueueItems() {
    try {
      const locationId = await auth.getLocationIdAsync();
      if (!locationId) {
        console.log("Cannot load queue - no location_id set");
        return;
      }

      const db = await dbClient.open();
      const range = IDBKeyRange.bound(
        [locationId, "0000-00-00T00:00:00.000Z"],
        [locationId, new Date().toISOString()]
      );

      const items = await db.getAllFromIndex("api_queue", "location_id, date", range);
      setQueueItems(items);

      // Fetch order/customer names for order-related requests
      const names = {};
      for (const item of items) {
        const method = item.request?.method;
        const url = item.request?.url;
        const parsedUrl = parseApiUrl(url);

        if (!parsedUrl || parsedUrl.resource !== 'orders') {
          continue;
        }

        let orderId = null;
        if (method === 'POST' && !parsedUrl.id) {
          orderId = item.id;
        } else if (item.handle) {
          orderId = Number(item.handle);
        } else if (parsedUrl.id) {
          orderId = parsedUrl.id;
        }
        let order = orderId ? await db.get('offline_orders', orderId) : null;
        if (order) {
          names[item.id] = customerName(order);
        }
      }
      setOrderNames(names);
    } catch (error) {
      console.error("Error loading queue items:", error);
    } finally {
      setLoading(false);
    }
  }

  function getRequestType(method, url) {
    if (url.includes('/orders') && url.includes('/signature') && method === 'POST') return 'Upload Signature';
    if (url.includes('/orders') && url.includes('/license') && method === 'POST') return 'Upload License';
    if (url.includes('/orders') && method === 'POST') return 'Create Order';
    if (url.includes('/orders') && method === 'PUT') return 'Update Order';
    if (url.includes('/orders') && method === 'DELETE') return 'Delete Order';
    if (url.includes('/upload') && method === 'POST') return 'Upload File';
    if (url.includes('/delete-file') && method === 'POST') return 'Delete File';
    return `Other`;
  }

  function formatDate(dateString) {
    return formatTime(new Date(dateString), "yyyy-MM-dd HH:mm:ss");
  }

  function handleCloseModal() {
    setShowModal(false);
  }

  function handleShowModal() {
    setShowModal(true);
  }

  function showToastMessage(message) {
    setToastMessage(message);
    setToastTrigger(prev => prev + 1);
  }

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Offline Queue Management</h2>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>Queued Requests ({queueItems.length})</span>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  showToastMessage("Sync started. Check the browser console for results.");
                  syncDb();
                }}
                className="me-2"
              >
                Sync Now
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {queueItems.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No requests in the offline queue.</p>
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Name</th>
                  <th>URL</th>
                  <th>Queued At</th>
                  <th>Dependencies</th>
                  <th style={{ minWidth: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queueItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>
                      <span className="badge bg-secondary">
                        {getRequestType(item.request.method, item.request.url)}
                      </span>
                    </td>
                    <td>{orderNames[item.id] || ''}</td>
                    <td>
                      <code className="small">
                        {item.request.method} {item.request.url}
                      </code>
                    </td>
                    <td>{formatDate(item.date)}</td>
                    <td>
                      {item.handle ? (
                        <span className="text-muted">Depends on {item.handle}</span>
                      ) : (
                        <span className="text-success">Independent</span>
                      )}
                    </td>
                    <td>
                      <Button variant="warning" size="sm" className="me-2" onClick={async () => {
                        await apiQueue.skip(item.id);
                        // Store toast message in localStorage before reload
                        localStorage.setItem('offlineQueueToast', "The associated requests have been moved to the end of the queue");
                        window.location.reload();
                      }}>Skip</Button>
                      <Button variant="danger" size="sm" onClick={() => {
                        setModalConfig({
                          title: "Delete Request",
                          body: "Are you sure you want to delete this queued request (and any dependent requests)? This action cannot be undone.",
                          primaryButtonText: "Delete",
                          primaryButtonHandler: async () => {
                            await apiQueue.delete(item.id);
                            handleCloseModal();
                            window.location.reload();
                          }
                        });
                        handleShowModal();
                      }}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <OfflineOrdersTable showExport={true} showDelete={true} />
      <Toast trigger={toastTrigger} message={toastMessage} />
      {modalConfig && (
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{modalConfig.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modalConfig.body}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="danger" onClick={modalConfig.primaryButtonHandler}>
              {modalConfig.primaryButtonText}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default OfflineQueue; 